import * as React from "react";
import { DataGrid } from "@mui/x-data-grid";
import {
  Box,
  Paper,
  Typography,
  Button,
  Modal,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import axios from "axios";
import { localeTextVietnamese } from "../../utils/localeText-vn";
import { generateSlug } from "../../utils/CurrencyFormat";
import { toast } from "react-toastify";
import swal from "sweetalert";
import TextEditor from "../TextEditor";
import TextEditorModal from "../TextEditorModal";

export default function ProductPage() {
  const [rows, setRows] = React.useState([]);
  const [images, setImages] = React.useState([]);
  const [categories, setCategories] = React.useState([]);
  const [open, setOpen] = React.useState(false);
  const [formData, setFormData] = React.useState({
    name: ``,
    image: ``,
    description: ``,
    slug: ``,
    price: 0,
    categoryId: 0,
  });
  const [errors, setErrors] = React.useState({});
  const [totalItems, setTotalItems] = React.useState(0);
  const [paginationModel, setPaginationModel] = React.useState({
    page: 0,
    pageSize: 10,
  });
  const [openModelImage, setOpenModelImage] = React.useState(false);

  const handleShowImage = () => {
    setOpenModelImage(true);
  };

  const handleCloseImage = () => {
    setOpenModelImage(false);
  };

  const handleChooseImage = (path) => {
    setFormData({ ...formData, image: path });
    setErrors({ ...errors, image: `` });
    handleCloseImage();
  };

  // setting collumn
  const columns = [
    { field: "index", headerName: "STT", width: 100 },
    { field: "name", headerName: "Tên sản phẩm", flex: 3 },
    { field: "slug", headerName: "Slug", flex: 3 },
    {
      field: "image",
      headerName: "Hình ảnh",
      flex: 3,
      renderCell: (params) => (
        <Box
          component="img"
          sx={{
            width: 230,
            height: 130,
            objectFit: "cover",
            borderRadius: "8px",
          }}
          alt={params.row.title}
          src={params.value}
        />
      ),
    },
    { field: "price", headerName: "Giá tiền", flex: 2 },
    { field: "salePrice", headerName: "Giảm giá", flex: 2 },
    {
      field: "categoryId",
      headerName: "Danh mục",
      flex: 2,
      renderCell: (params) => (
        <span>{getCategoryName(params.value) || "Loading..."}</span>
      ),
    },
    {
      field: "action",
      headerName: "Thao tác",
      description: "",
      sortable: false,
      flex: 3,
      renderCell: (params) => (
        <Box
          sx={{ display: "flex", gap: 1, alignItems: "center", height: "100%" }}
        >
          <Button
            variant="contained"
            color="warning"
            onClick={(event) => {
              event.stopPropagation();
              handleEdit(params.row);
            }}
          >
            Chỉnh sửa
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={(event) => {
              event.stopPropagation();
              handleDelete(params.id);
            }}
          >
            Xoá
          </Button>
        </Box>
      ),
    },
  ];

  const handleEdit = (row) => {
    handleOpen(row);
  };

  React.useEffect(() => {
    getData();
    getCategory();
  }, [paginationModel]);

  const getData = () => {
    axios
      .get(`/api/v1/products`, {
        params: {
          page: paginationModel.page,
          limit: paginationModel.pageSize,
          paginationAdmin: true,
        },
      })
      .then((res) => {
        setRows(res.data.products);
        setTotalItems(res.data.totalItems);
      })
      .catch((err) => console.log(err));
  };

  const getImage = () => {
    axios
      .get(`/api/v1/images`, {
        params: {},
      })
      .then((res) => {
        setImages(res.data.images);
      })
      .catch((err) => console.log(err));
  };

  const getCategory = () => {
    axios
      .get(`/api/v1/categories`, {
        params: {},
      })
      .then((res) => {
        setCategories(res.data.categories);
      })
      .catch((err) => console.log(err));
  };

  const getCategoryName = (id) => {
    const category = categories.find((cat) => cat.id === id);
    return category ? category.name : "Unknown";
  };

  const rowsWithIndex = rows.map((row, index) => ({
    index: index + 1,
    ...row,
  }));

  const handleOpen = (data) => {
    setOpen(true);
    getImage();
    if (data) {
      setFormData(data);
    }
  };

  const handleClose = (isReload) => {
    setOpen(!open);
    setFormData({});
    setErrors({});
    if (isReload) {
      getData();
    }
  };

  const handleSelectChangeImage = (e) => {
    setErrors({ ...errors, image: "" });
    setFormData({
      ...formData,
      image: e.target.value,
    });
  };

  const handleSelectChangeCategory = (e) => {
    setErrors({ ...errors, categoryId: "" });
    setFormData({
      ...formData,
      categoryId: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = await validateForm(formData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
    } else {
      const sendData = {
        name: formData.name,
        image: formData.image,
        price: formData.price,
        salePrice: formData.salePrice,
        description: formData.description,
        slug: formData.slug,
        categoryId: formData.categoryId,
      };
      axios
        .post(`/api/v1/products`, sendData)
        .then((res) => {
          toast("Thêm sản phẩm thành công!", {
            type: "success",
            autoClose: 1000,
          });
          handleClose(true);
        })
        .catch((err) => {
          console.log(err);
          toast(err.response.data.errors[0].msg, {
            type: "error",
            autoClose: 1000,
          });
        });
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    const validationErrors = await validateForm(formData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
    } else {
      const sendData = {
        name: formData.name,
        image: formData.image,
        price: formData.price,
        salePrice: formData.salePrice,
        description: formData.description,
        slug: formData.slug,
        categoryId: formData.categoryId,
      };
      axios
        .put(`/api/v1/products/` + formData.id, sendData)
        .then((res) => {
          toast("Cập nhật sản phẩm thành công!", {
            type: "success",
            autoClose: 1000,
          });
          handleClose(true);
        })
        .catch((err) => {
          console.log(err);
          toast("Có lỗi xảy ra!", {
            type: "error",
            autoClose: 1000,
          });
        });
    }
  };

  const validateForm = async (data) => {
    const errors = {};
    console.log(data.content);

    if (!data.name || data.name.trim() === "") {
      errors.name = "Tên sản phẩm không được để trống.";
    }

    if (!data.slug || data.slug.trim() === "") {
      errors.slug = "Slug sản phẩm không được để trống.";
    }

    if (!data.price || data.price === 0) {
      errors.price = "Giá tiền sản phẩm không được để trống.";
    }

    if (
      !data.description ||
      data.description.trim() === "" ||
      data.description.trim() === "<p><br></p>"
    ) {
      errors.description = "Mô tả sản phẩm không được để trống.";
    }

    if (!data.image || data.image.trim() === "") {
      errors.image = "Hình ảnh sản phẩm không được để trống.";
    }

    if (!data.categoryId || data.categoryId === 0) {
      errors.categoryId = "Danh mục sản phẩm không được để trống.";
    }

    return errors;
  };

  const handleDelete = (id) => {
    swal({
      title: `Bạn muốn xóa sản phẩm này`,
      text: ` Lưu ý: Hành động không thể khôi phục lại`,
      icon: "warning",
      buttons: ["Đóng", "Xóa"],
      successMode: true,
    }).then((ok) => {
      if (ok) {
        axios
          .delete(`/api/v1/products/${id}`)
          .then((res) => {
            toast("Xoá sản phẩm thành công", {
              type: "success",
              autoClose: 1000,
            });
            getData();
          })
          .catch((err) => {
            console.log(err);
          });
      }
    });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Quản lý sản phẩm
      </Typography>
      <Box sx={{ marginBottom: 2, display: "flex", justifyContent: "end" }}>
        <Button
          variant="contained"
          color="info"
          sx={{ width: 100 }}
          onClick={handleOpen}
        >
          Thêm
        </Button>
      </Box>
      <Paper sx={{ height: 730, width: "100%" }}>
        <DataGrid
          rows={rowsWithIndex}
          columns={columns}
          rowHeight={150}
          rowCount={totalItems}
          pagination
          paginationMode="server"
          onPaginationModelChange={(newPaginationModel) =>
            setPaginationModel(newPaginationModel)
          }
          initialState={{
            pagination: {
              paginationModel: paginationModel,
            },
          }}
          pageSizeOptions={[5, 10, 50, 100]}
          // checkboxSelection
          disableRowSelectionOnClick
          localeText={localeTextVietnamese}
          sx={{
            "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
              outline: "none",
            },
            "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
              {
                marginBottom: 0,
              },
          }}
        />
      </Paper>

      {/* Add Edit Modal */}
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box
          sx={{
            width: "100vw",
            height: "100vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Paper
            sx={{
              width: "50vw",
              height: "95vh",
              p: 5,
              overflowY: "auto",
              overflowX: "hidden",
            }}
          >
            <Typography sx={{ pb: 5 }} variant="h4" component="h2">
              {formData.id ? "Sửa" : "Thêm"} sản phẩm
            </Typography>
            <TextField
              label="Tên sản phẩm"
              required
              variant="outlined"
              sx={{ width: "100%", paddingBottom: 5 }}
              value={formData.name ?? ""}
              onChange={(e) => {
                setFormData({
                  ...formData,
                  name: e.target.value,
                  slug: generateSlug(e.target.value),
                });
                setErrors({ ...errors, name: "", slug: "" });
              }}
              error={Boolean(errors.name)}
              helperText={errors.name}
            />
            <TextField
              label="Slug"
              required
              variant="outlined"
              sx={{ width: "100%", paddingBottom: 5 }}
              value={formData.slug ?? ""}
              onChange={(e) => {
                setFormData({ ...formData, slug: e.target.value });
                setErrors({ ...errors, slug: "" });
              }}
              error={Boolean(errors.slug)}
              helperText={errors.slug}
            />
            <TextField
              label="Giá tiền"
              required
              variant="outlined"
              type="number"
              sx={{ width: "100%", paddingBottom: 5 }}
              value={formData.price ?? ""}
              onChange={(e) => {
                const value = e.target.value;
                const validValue = value.match(/^\d+(\.\d{0,2})?$/)
                  ? value
                  : formData.price;
                setFormData({ ...formData, price: validValue });
                setErrors({ ...errors, price: "" });
              }}
              error={Boolean(errors.price)}
              helperText={errors.price}
            />
            <TextField
              label="Giá giảm"
              variant="outlined"
              type="number"
              sx={{ width: "100%", paddingBottom: 5 }}
              value={formData.salePrice ?? ""}
              onChange={(e) => {
                const value = e.target.value;
                const validValue = value.match(/^\d+(\.\d{0,2})?$/)
                  ? value
                  : formData.salePrice;
                setFormData({ ...formData, salePrice: validValue });
              }}
            />
            <Box sx={{ pb: 5 }}>
              <TextEditor
                setContents={formData.description}
                onChange={(e) => {
                  setErrors((prevFormData) => ({
                    ...prevFormData,
                    description: "",
                  }));
                  setFormData((prevFormData) => ({
                    ...prevFormData,
                    description: e,
                  }));
                }}
                placeholder={"Nội dung"}
                style={{
                  border: errors.description
                    ? "1px solid red"
                    : "1px solid #dadada",
                  padding: 16,
                }}
              />
              {errors.description && (
                <p className="MuiFormHelperText-root Mui-error MuiFormHelperText-sizeMedium MuiFormHelperText-contained Mui-required css-er619e-MuiFormHelperText-root">
                  {errors.description}
                </p>
              )}
            </Box>
            {formData.image ? (
              <Box
                component="img"
                sx={{
                  width: 300,
                  height: 200,
                  objectFit: "cover",
                  borderRadius: "8px",
                  mb: 2,
                }}
                alt={formData.title}
                src={formData.image}
              />
            ) : (
              <Box sx={{ mb: 2 }}>Không có hình ảnh tiêu đề</Box>
            )}
            {errors.image && (
              <p className="MuiFormHelperText-root Mui-error MuiFormHelperText-sizeMedium MuiFormHelperText-contained Mui-required css-er619e-MuiFormHelperText-root">
                {errors.image}
              </p>
            )}
            <Box sx={{ pb: 2 }}>
              <Button
                variant="contained"
                color="success"
                onClick={handleShowImage}
                sx={{ mb: 1 }}
              >
                Chọn hình ảnh
              </Button>
            </Box>
            <FormControl fullWidth sx={{ pb: 5 }}>
              <InputLabel id="image-label" error={Boolean(errors.categoryId)}>
                Chọn danh mục *
              </InputLabel>
              <Select
                labelId="image-label"
                value={formData.categoryId ?? ``}
                onChange={handleSelectChangeCategory}
                label="Chọn danh mục *"
                error={Boolean(errors.categoryId)}
              >
                {categories.map((item, idx) => (
                  <MenuItem key={idx} value={item.id}>
                    {item.name}
                  </MenuItem>
                ))}
              </Select>
              {errors.categoryId && (
                <p className="MuiFormHelperText-root Mui-error MuiFormHelperText-sizeMedium MuiFormHelperText-contained Mui-required css-er619e-MuiFormHelperText-root">
                  {errors.categoryId}
                </p>
              )}
            </FormControl>
            <Box sx={{ display: "flex", justifyContent: "end", gap: 3 }}>
              <Button
                variant="contained"
                color="secondary"
                onClick={handleClose}
              >
                Trở về
              </Button>
              <Button
                variant="contained"
                color="info"
                onClick={formData.id ? handleUpdate : handleSubmit}
              >
                Lưu
              </Button>
            </Box>
          </Paper>
        </Box>
      </Modal>

      {openModelImage ? (
        <TextEditorModal
          handleCloseModal={handleCloseImage}
          showModal={openModelImage}
          onCopyPath={handleChooseImage}
        />
      ) : null}
    </Box>
  );
}
